<script setup lang="ts">
import { computed, reactive, watch } from 'vue'
import Drawer from '@uiKit/elements/Drawer.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import AppButton from '@uiKit/elements/Button.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import type { TransactionFilters } from '../../transactions/types/transactionTypes'
import type { TransactionFilterOption } from '../../transactions/api/transactionParser'

interface FilterChipOption {
  value: string
  label: string
}

interface Props {
  open: boolean
  filters: TransactionFilters
  // Server-driven options (same source as the old popover panel). When empty
  // the FALLBACK_* lists render so the drawer is never chip-less.
  typeOptions?: TransactionFilterOption[]
  statusOptions?: TransactionFilterOption[]
}

const props = withDefaults(defineProps<Props>(), {
  typeOptions: () => [],
  statusOptions: () => [],
})

const emit = defineEmits<{
  (e: 'close'): void
  // Apply commits the draft + closes (parent handles both).
  (e: 'apply', filters: TransactionFilters): void
  // Reset wipes the committed query state (parent) AND the local draft.
  (e: 'clear'): void
}>()

// Draft state lives inside the drawer — committed only on Apply.
const draft = reactive<TransactionFilters>({ ...props.filters })

// When the drawer opens or the committed filters change underneath us, sync
// the draft so the chips show what's currently applied. Closing without
// applying does NOT mutate anything — we re-seed from props on the next open.
watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) Object.assign(draft, props.filters)
  },
)
watch(
  () => props.filters,
  (next) => {
    if (props.open) Object.assign(draft, next)
  },
  { deep: true },
)

const FALLBACK_TYPE_OPTIONS: FilterChipOption[] = [
  { value: '', label: 'All' },
  { value: 'deposit', label: 'Deposit' },
  { value: 'withdrawal', label: 'Withdrawal' },
  { value: 'investment', label: 'Investment' },
  { value: 'rental_income', label: 'Rental Income' },
  { value: 'refund', label: 'Refund' },
  { value: 'token_received', label: 'Token Received' },
]

const FALLBACK_STATUS_OPTIONS: FilterChipOption[] = [
  { value: '', label: 'All' },
  { value: 'completed', label: 'Completed' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'failed', label: 'Failed' },
  { value: 'reversed', label: 'Reversed' },
]

const typeOptionsList = computed<FilterChipOption[]>(() => {
  if (!props.typeOptions.length) return FALLBACK_TYPE_OPTIONS
  return [{ value: '', label: 'All' }, ...props.typeOptions]
})

const statusOptionsList = computed<FilterChipOption[]>(() => {
  if (!props.statusOptions.length) return FALLBACK_STATUS_OPTIONS
  return [{ value: '', label: 'All' }, ...props.statusOptions]
})

const activeCount = computed(
  () =>
    (draft.type ? 1 : 0) + (draft.status ? 1 : 0) + (draft.dateFrom || draft.dateTo ? 1 : 0),
)

const onApply = () => {
  emit('apply', { ...draft })
  emit('close')
}

const onReset = () => {
  draft.type = ''
  draft.status = ''
  draft.dateFrom = ''
  draft.dateTo = ''
  emit('clear')
}

const onClose = () => emit('close')
</script>

<template>
  <Drawer
    :model-value="open"
    position="right"
    width="420px"
    :show-close-icon="false"
    @update:model-value="(v: boolean) => !v && onClose()"
  >
    <template #header>
      <Box align="center" justify="between" gap="2" class="wafd__header">
        <Text type="page-title">Filter Transactions</Text>
        <Box
          is="button"
          align="center"
          justify="center"
          class="wafd__close"
          type="button"
          aria-label="Close filters"
          @click="onClose"
        >
          <PiIcon name="times" />
        </Box>
      </Box>
    </template>

    <Box direction="col" class="wafd__shell">
      <Box direction="col" gap="4" class="wafd__body">
        <Box direction="col" gap="3">
          <Text type="caption-sm" transform="uppercase" is="span">Transaction Type</Text>
          <Box gap="2" class="wafd__chips">
            <Box
              v-for="opt in typeOptionsList"
              :key="`type-${opt.value}`"
              is="button"
              class="wafd__chip"
              :class="{ 'wafd__chip--active': draft.type === opt.value }"
              type="button"
              :aria-label="`Filter by type ${opt.label}`"
              :aria-pressed="draft.type === opt.value"
              @click="draft.type = opt.value"
            >
              <Text type="caption" weight="medium" color="default" is="span">
                {{ opt.label }}
              </Text>
            </Box>
          </Box>
        </Box>

        <Box direction="col" gap="3">
          <Text type="caption-sm" transform="uppercase" is="span">Status</Text>
          <Box gap="2" class="wafd__chips">
            <Box
              v-for="opt in statusOptionsList"
              :key="`status-${opt.value}`"
              is="button"
              class="wafd__chip"
              :class="{ 'wafd__chip--active': draft.status === opt.value }"
              type="button"
              :aria-label="`Filter by status ${opt.label}`"
              :aria-pressed="draft.status === opt.value"
              @click="draft.status = opt.value"
            >
              <Text type="caption" weight="medium" color="default" is="span">
                {{ opt.label }}
              </Text>
            </Box>
          </Box>
        </Box>

        <Box direction="col" gap="3">
          <Text type="caption-sm" transform="uppercase" is="span">Date Range</Text>
          <Box gap="2">
            <Box direction="col" gap="1" grow class="wafd__date-field">
              <Text type="caption-sm" color="muted" is="label">From</Text>
              <!-- Native date input — no kit citizen yet. §18 exception. -->
              <input v-model="draft.dateFrom" class="wafd__date-input" type="date" />
            </Box>
            <Box direction="col" gap="1" grow class="wafd__date-field">
              <Text type="caption-sm" color="muted" is="label">To</Text>
              <input v-model="draft.dateTo" class="wafd__date-input" type="date" />
            </Box>
          </Box>
        </Box>
      </Box>

      <Box align="center" gap="3" class="wafd__footer">
        <AppButton
          variant="text"
          color="primary"
          label="Reset Filters"
          :disabled="activeCount === 0"
          class="wafd__reset"
          @click="onReset"
        />
        <AppButton
          :label="`Apply${activeCount > 0 ? ` (${activeCount})` : ''} Filter`"
          class="wafd__apply"
          @click="onApply"
        />
      </Box>
    </Box>
  </Drawer>
</template>

<style scoped>
.wafd__header {
  width: 100%;
}

.wafd__close {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: 1px solid var(--p-content-border-color);
  background: var(--p-surface-0);
  cursor: pointer;
  color: var(--p-text-muted-color);
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s;
}

.wafd__close:hover {
  background: var(--p-surface-100);
  color: var(--p-text-color);
}

.wafd__shell {
  height: 100%;
  min-height: 0;
}

.wafd__body {
  flex: 1;
  min-height: 0;
  padding: 1.25rem 1.5rem;
  overflow-y: auto;
  scrollbar-width: none;
}

.wafd__body::-webkit-scrollbar {
  display: none;
}

.wafd__chips {
  flex-wrap: wrap;
}

.wafd__chip {
  background: transparent;
  border: 1px solid var(--p-primary-400);
  border-radius: 20px;
  cursor: pointer;
  line-height: 1.5;
  padding: 4px 12px;
  white-space: nowrap;
  transition:
    background 0.12s,
    border-color 0.12s;
}

.wafd__chip--active {
  background: var(--p-primary-100);
  border-color: var(--p-primary-600);
}

.wafd__chip:focus-visible {
  outline: 2px solid var(--p-primary-600);
  outline-offset: 2px;
}

.wafd__date-field {
  min-width: 0;
}

.wafd__date-input {
  width: 100%;
  height: 38px;
  border: 1px solid var(--p-surface-300);
  border-radius: 8px;
  padding: 0 10px;
  font-size: 13px;
  color: var(--p-text-color);
  background: var(--p-surface-0);
  box-sizing: border-box;
  outline: none;
  font-family: inherit;
  transition: border-color 0.12s;
}

.wafd__date-input:focus {
  border-color: var(--p-primary-600);
}

.wafd__footer {
  padding: 14px 1.5rem;
  border-top: 1px solid var(--p-content-border-color);
  background: var(--p-surface-0);
  flex-shrink: 0;
}

.wafd__reset {
  flex: 0 0 auto;
}

.wafd__apply {
  flex: 1;
}
</style>
